/**
 * Privacy Policy — public route, no auth required.
 *
 * Serves the launch-approved privacy content directly from the SPA so that
 * the App Store 5.1.1 requirement ("privacy policy accessible without an
 * account") is met on `app.futureself.es/privacy`. This page is deliberately
 * unauthenticated and never redirects to `/login`.
 *
 * Content is the authoritative launch copy referenced in
 * /app/memory/LAUNCH_CHECKLIST.md §12.1 (2026-07-25).
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import useSmartBack from '../lib/useSmartBack';

const LAST_UPDATED = '29 July 2026';

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid={`privacy-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="font-serif text-2xl text-[#DCD3CA] mb-3">{title}</h2>
    <div className="text-[#B5B1AB] font-sans text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Privacy = () => {
  const smartBack = useSmartBack();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof document !== 'undefined') {
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
  }, []);
  return (
  <div
    className="min-h-[100dvh] bg-[#1C1C1E] text-[#DCD3CA]"
    style={{
      paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
      paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
    }}
    data-testid="privacy-page"
  >
    <div className="max-w-2xl mx-auto px-6">
      <button
        type="button"
        onClick={smartBack}
        className="inline-flex items-center gap-2 text-[#8BAA9E] font-sans text-sm mb-8 hover:text-[#A3C2B6]"
        data-testid="privacy-back-link"
        aria-label="Back"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Future Self
      </button>

      <header className="mb-10">
        <p className="text-[#8BAA9E] font-sans text-xs uppercase tracking-[2px] mb-3">FutureSelf</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F3EF] mb-3">Privacy Policy</h1>
        <p className="text-[#7A7670] font-sans text-xs">Last updated: {LAST_UPDATED}</p>
      </header>

      <Section title="Who We Are">
        <p>
          Future Self is operated from Ireland. This policy explains how we handle personal data when you use the
          Future Self mobile app and website at <span className="text-[#DCD3CA]">app.futureself.es</span>.
        </p>
      </Section>

      <Section title="Information We Collect">
        <p>
          <strong className="text-[#DCD3CA]">Account information:</strong> your email address (and optional
          display name) so you can sign in and receive account emails such as password resets.
        </p>
        <p>
          <strong className="text-[#DCD3CA]">Content you create:</strong> journal entries, gratitude notes,
          identity statements, goals and any photos or videos you upload as visualisation backgrounds. This
          content is stored privately in your account and is never shared with other users, sold, or used for
          advertising.
        </p>
        <p>
          <strong className="text-[#DCD3CA]">Subscription state:</strong> if you subscribe to Premium, we store
          the subscription status returned by Stripe (on the web) or by Apple/Google (on mobile). We never see
          your payment card details.
        </p>
        <p>
          <strong className="text-[#DCD3CA]">Optional analytics:</strong> when — and only when — you explicitly
          grant consent, we use PostHog EU to record anonymous product usage (screens viewed, actions taken).
          Personal content (journal entries, gratitude notes, uploaded media) is never included in analytics
          events. You can revoke this consent at any time from Settings.
        </p>
        <p>
          <strong className="text-[#DCD3CA]">Community features (retired July 2026).</strong> A public
          community feed called &ldquo;The Collective&rdquo; was previously available in Future Self. It was retired on
          25 July 2026. Future Self no longer collects, stores, publishes or moderates any user-authored public
          content, and no data is shared between users.
        </p>
      </Section>

      <Section title="How We Use Your Data">
        <p>
          We use your data solely to operate the app you signed up for: showing you your journal and goals,
          sending account emails such as password resets, generating personalised affirmations, and
          maintaining your subscription.
        </p>
        <p>We do not sell your data. We do not use it for advertising. We do not share it with data brokers.</p>
      </Section>

      <Section title="Third-Party Services">
        <p>The following services process data strictly on our behalf:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong className="text-[#DCD3CA]">Stripe</strong> and <strong className="text-[#DCD3CA]">RevenueCat</strong> — subscription billing.</li>
          <li><strong className="text-[#DCD3CA]">Resend</strong> — sending password-reset and account emails.</li>
          <li><strong className="text-[#DCD3CA]">ElevenLabs</strong> — voice narration for your visualisations.</li>
          <li><strong className="text-[#DCD3CA]">Google (Gemini)</strong> — generating your affirmations.</li>
          <li><strong className="text-[#DCD3CA]">PostHog EU</strong> — anonymous product analytics (consent-only, EU-hosted).</li>
          <li><strong className="text-[#DCD3CA]">Apple / Google</strong> — Sign in with Apple, Google Sign-In, and native subscription billing on their respective platforms.</li>
        </ul>
      </Section>

      <Section title="Your Rights">
        <p>
          Under GDPR you may access, correct, export or delete your personal data at any time. The fastest way
          is Settings → Delete Account, which permanently removes your data across all our systems. You may
          also email <span className="text-[#DCD3CA]">support@futureself.es</span> for any request we cannot
          fulfil in-app. If you believe we have not handled your data appropriately, you have the right to
          lodge a complaint with the Irish Data Protection Commission (<span className="text-[#DCD3CA]">dataprotection.ie</span>)
          or with the supervisory authority in your EU country of residence.
        </p>
      </Section>

      <Section title="Data Retention">
        <p>
          We keep your data for as long as your account exists. When you delete your account, your personal
          content is removed immediately from primary storage and permanently purged from backups within 30 days.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about this policy: <span className="text-[#DCD3CA]">support@futureself.es</span>.
        </p>
      </Section>

      <footer className="pt-8 mt-8 border-t border-white/5 text-[#7A7670] font-sans text-xs">
        <p>Future Self · Made with care in Ireland.</p>
        <p className="mt-2">
          See also our{' '}
          <Link to="/terms" className="text-[#8BAA9E] hover:text-[#A3C2B6]" data-testid="privacy-to-terms-link">
            Terms of Use
          </Link>
          .
        </p>
      </footer>
    </div>
  </div>
  );
};

export default Privacy;
